import styled from 'styled-components';

/**
 * Concat given property names with className if the properties value is truthy.
 * @param props properties of a StyledComponent
 * @param propNames property names to be used as css classes if the property value is truthy
 * @returns css class names as string
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function generateClassNameByPropNames(
  props: Record<string, any>,
  ...propNames: string[]
): string {
  const combineTruthyPropNames = (total: string, propName: string) =>
    propName in props ? total + `${propName} ` : total;
  return propNames.reduce(combineTruthyPropNames, '') + (props.className ?? '');
}

export interface ButtonProps {
  /** primary button theme */
  primary?: boolean;
  /** secondary button theme */
  secondary?: boolean;
  /** plain button theme */
  plain?: boolean;
  /** show load spinner and disable clicks */
  loading?: boolean;
}

/** Button with primary/secondary styling and loading. From unified login */
export const Button = styled.button.attrs<ButtonProps>((props) => ({
  type: 'button',
  className: generateClassNameByPropNames(
    props,
    'primary',
    'secondary',
    'plain',
    'loading'
  ),
  'aria-busy': props.loading ?? false,
}))<ButtonProps>`
  @keyframes circle__svg {
    0% {
      transform: rotateZ(0deg);
    }
    100% {
      transform: rotateZ(360deg);
    }
  }

  min-height: 48px;
  padding: 12px 24px 12px 24px;
  color: var(--background);
  text-align: center;
  font-size: 18px;
  vertical-align: middle;
  border: none;
  border-radius: 24px;
  transition: background-color 0.1s ease;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5em;
  font-weight: 500;

  &.primary {
    flex: 1;

    &:enabled {
      background-color: var(--secondary);
    }

    &:disabled {
      background-color: var(--secondary-disabled);
    }

    &:not(:disabled) {
      cursor: pointer;

      &:hover {
        color: var(--background);
        background-color: var(--secondary-hover);
      }

      &:active {
        color: var(--background);
        background-color: var(--secondary-active);
      }
    }
  }

  &.secondary {
    flex: 1;

    &:enabled {
      color: var(--secondary);
      background-color: var(--background);
      border: 2px solid var(--secondary);
    }

    &:disabled {
      color: var(--secondary-disabled);
      background-color: var(--background);
      border: 2px solid var(--secondary-disabled);
    }

    &:not(:disabled) {
      cursor: pointer;

      &:hover {
        color: var(--background);
        background-color: var(--secondary-hover);
        border-color: var(--secondary-hover);
      }

      &:active {
        color: var(--background);
        background-color: var(--secondary-active);
        border-color: var(--secondary-active);
      }
    }
  }

  &.plain {
    align-items: center;
    background-color: transparent;
    gap: 16px;
    color: var(--colour-secondary);

    padding: 8px;
    align-self: flex-start;
    border-radius: 12px;
    flex: 1;

    &:not(:disabled) {
      cursor: pointer;
    }

    &:disabled {
      color: var(--secondary-disabled);
      background-color: var(--background);
      border: 2px solid var(--secondary-disabled);
    }

    &:hover {
      background-color: var(--colour-grey1);
    }

    img {
      font-size: 32px;
    }
  }

  &[aria-busy='true'] {
    pointer-events: none;
    user-select: none;

    ::before {
      animation: circle__svg 0.75s linear infinite;
      transform: translateY(-50%);
      width: 25px;
      height: 25px;
      content: url("data:image/svg+xml,%3Csvg viewBox='-25 -25 250 250' version='1.1' xmlns='http://www.w3.org/2000/svg' style='transform:rotate(-90deg)'%3E%3Ccircle r='90' cx='100' cy='100' fill='transparent' stroke='%23ffffff' stroke-width='16px' stroke-dasharray='565.48px' stroke-dashoffset='0'%3E%3C/circle%3E%3Ccircle r='90' cx='100' cy='100' stroke='%23196cff' stroke-width='10' stroke-linecap='round' stroke-dashoffset='118.692px' fill='transparent' stroke-dasharray='565.48px'%3E%3C/circle%3E%3C/svg%3E");
    }
  }
`;
